import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./TestPage.css";
import search from "../../../assets/search.png";

type TestQuestion = {
  number: number;
  id: number;
  text: string;
  answer1: string;
  answer2: string;
  answer3: string;
  answer4: string;
};

const getQuestionOptions = (question: TestQuestion) => [
  { number: 1, text: question.answer1 },
  { number: 2, text: question.answer2 },
  { number: 3, text: question.answer3 },
  { number: 4, text: question.answer4 },
];

const MOSCOW_TIME_ZONE = "Europe/Moscow";

const formatCourseDate = (value?: string | null) => {
  const date = value ? new Date(value) : new Date();
  return Number.isNaN(date.getTime())
    ? new Date().toLocaleDateString("ru-RU", { timeZone: MOSCOW_TIME_ZONE })
    : date.toLocaleDateString("ru-RU", { timeZone: MOSCOW_TIME_ZONE });
};

const formatTestTime = (time: number) => {
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = time % 60;

  return [hours, minutes, seconds]
    .map((part) => part.toString().padStart(2, "0"))
    .join(":");
};

const formatStudentDisplayName = (fullName: string) => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[1]} ${parts[0]}`;
  }

  return fullName.trim();
};

const TestPage: React.FC = () => {
  const [questions, setQuestions] = useState<TestQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentQuestion, setCurrentQuestion] = useState<number>(() => {
    const savedQuestion = localStorage.getItem("currentQuestion");
    return savedQuestion ? parseInt(savedQuestion, 10) : 0;
  });
  const [answers, setAnswers] = useState<number[]>([]);
  const [timeLeft, setTimeLeft] = useState<number>(() => {
    const savedTime = localStorage.getItem("timeLeft");
    return savedTime ? parseInt(savedTime, 10) : 3600;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const questionRefs = useRef<HTMLDivElement[]>([]);
  const navigate = useNavigate();
  const studentFullName = localStorage.getItem("fullName") || "";
  const studentDisplayName = formatStudentDisplayName(studentFullName);

  useEffect(() => {
    const loadQuestions = async () => {
      const savedQuestions = localStorage.getItem("testQuestions");
      const savedAnswers = localStorage.getItem("answers");

      if (savedQuestions) {
        const parsedQuestions = JSON.parse(savedQuestions) as TestQuestion[];
        setQuestions(parsedQuestions);
        setAnswers(
          savedAnswers
            ? JSON.parse(savedAnswers)
            : Array(parsedQuestions.length).fill(0)
        );
        setLoading(false);
        return;
      }

      const username = localStorage.getItem("username") || "";
      const specialty = localStorage.getItem("specialty") || "";

      try {
        const params = new URLSearchParams({ username, specialty });
        const response = await fetch(
          `/api/test-questions?${params.toString()}`
        );
        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data.error ||
              "Для вашей специальности база вопросов ещё не загружена. Обратитесь к администратору."
          );
        }

        setQuestions(data.questions);
        setAnswers(Array(data.questions.length).fill(0));
        localStorage.setItem("testAttemptId", data.attemptId);
        localStorage.setItem("testQuestions", JSON.stringify(data.questions));
        localStorage.setItem(
          "answers",
          JSON.stringify(Array(data.questions.length).fill(0))
        );
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Для вашей специальности база вопросов ещё не загружена. Обратитесь к администратору.";
        setErrorMessage(message);
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();
  }, []);

  const submitTest = useCallback(
    async (timeUp: boolean) => {
      if (isSubmitting || questions.length === 0) return;

      setIsSubmitting(true);

      const endTime = new Date().toISOString();
      localStorage.setItem("endTime", endTime);

      const username = localStorage.getItem("username") || "";
      const specialty = localStorage.getItem("specialty") || "";
      const attemptId = localStorage.getItem("testAttemptId") || "";
      const completedAttempts = JSON.parse(
        localStorage.getItem("attempts") || "[]"
      );
      const payloadAnswers = questions.map((question, index) => ({
        questionId: question.id,
        selectedAnswer: answers[index] || 0,
      }));

      try {
        const response = await fetch(
          "/api/check-test-result",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              username,
              attemptId,
              specialty,
              answers: payloadAnswers,
            }),
          }
        );
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Ошибка проверки результата");
        }

        const newAttempt = {
          id: completedAttempts.length + 1,
          status: "Завершены",
          submittedAt: new Date().toLocaleString("ru-RU", {
            timeZone: MOSCOW_TIME_ZONE,
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
          }),
          score: data.score,
          total: data.total,
          attemptId: data.attemptId,
          reviewLink: "/test-review",
          printLink: `/test-protocol/${data.attemptId}`,
        };

        localStorage.setItem(
          "attempts",
          JSON.stringify([...completedAttempts, newAttempt])
        );
        localStorage.setItem("lastAttemptId", data.attemptId);
        localStorage.removeItem("timeLeft");

        navigate("/test-review", { state: { answers, timeUp } });
        window.scrollTo({ top: 0 });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Ошибка проверки результата";
        setErrorMessage(message);
        setIsSubmitting(false);
      }
    },
    [answers, isSubmitting, navigate, questions]
  );

  useEffect(() => {
    if (!localStorage.getItem("startTime")) {
      localStorage.setItem("startTime", new Date().toISOString());
    }

    if (loading || errorMessage || questions.length === 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          submitTest(true);
          return 0;
        }

        const newTime = prevTime - 1;
        localStorage.setItem("timeLeft", newTime.toString());
        return newTime;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
      localStorage.setItem("currentQuestion", currentQuestion.toString());
      localStorage.setItem("answers", JSON.stringify(answers));
      localStorage.setItem("timeLeft", timeLeft.toString());
    };
  }, [
    answers,
    currentQuestion,
    errorMessage,
    loading,
    questions.length,
    submitTest,
    timeLeft,
  ]);

  const handleAnswerSelect = (answerNumber: number, questionIndex: number) => {
    const updatedAnswers = [...answers];
    updatedAnswers[questionIndex] = answerNumber;
    setAnswers(updatedAnswers);
    localStorage.setItem("answers", JSON.stringify(updatedAnswers));
  };

  const handleScrollToQuestion = (index: number) => {
    questionRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    setCurrentQuestion(index);
    localStorage.setItem("currentQuestion", index.toString());
  };

  const handleSubmit = () => {
    submitTest(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/");
  };

  if (loading) {
    return <div style={{ padding: "20px" }}>Загрузка вопросов...</div>;
  }

  if (errorMessage) {
    return <div style={{ padding: "20px" }}>{errorMessage}</div>;
  }

  return (
    <div className="test-page-container">
      <div className="title">
        <div className="timer">
          <span className="timer-label">Оставшееся время:</span>{" "}
          <span className="timer-value">{formatTestTime(timeLeft)}</span>
        </div>
        {studentFullName && (
          <div className="exam-user-menu">
            <button
              type="button"
              className="exam-user-button"
              onClick={() => setIsUserMenuOpen((isOpen) => !isOpen)}
              aria-haspopup="menu"
              aria-expanded={isUserMenuOpen}
            >
              <span className="exam-user-name">{studentDisplayName}</span>
              <span className="exam-user-avatar"></span>
              <span className="exam-user-caret" aria-hidden="true"></span>
            </button>
            {isUserMenuOpen && (
              <div className="exam-user-dropdown" role="menu">
                <button type="button" onClick={handleLogout} role="menuitem">
                  Выход
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="title22">
        <div>Тестирование - 1 -й этап</div>
        <div className="imgCont">
          <img src={search} alt="" style={{ width: 20 }} />
        </div>
      </div>
      <div className="course-breadcrumb">
        Мои курсы › Тестирование - 1-й этап{" "}
        {formatCourseDate(localStorage.getItem("startTime"))}
      </div>
      <div className="test-content">
        <div className="questions">
          <div className="questions-container">
            {questions.map((question, index) => (
              <div
                key={question.id}
                ref={(el) => (questionRefs.current[index] = el!)}
                className="question-cont"
              >
                <div className="question-title">
                  <div>
                    Вопрос <b>{index + 1}</b>
                  </div>
                  <div>{answers[index] ? "Ответ дан" : "Нет ответа"}</div>
                  <div>Балл: 1,00</div>
                </div>
                <div className="question-item">
                  <div>{question.text}</div>
                  <div className="options-list">
                    {getQuestionOptions(question).map((option) => (
                      <label key={option.number} style={{ cursor: "pointer" }}>
                        <input
                          type="radio"
                          name={`question-${index}`}
                          value={option.number}
                          checked={answers[index] === option.number}
                          onChange={() =>
                            handleAnswerSelect(option.number, index)
                          }
                          className="form_radio"
                        />
                        {option.number}. {option.text}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button
            className="finish-test2"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            Закончить попытку
          </button>
        </div>
        <div className="navigation-panel">
          <div className="nav-title">Навигация по тесту</div>
          {questions.map((_, index) => (
            <button
              key={index}
              className={`navigation-button ${answers[index] ? "answered" : ""}`}
              onClick={() => handleScrollToQuestion(index)}
            >
              {index + 1}
            </button>
          ))}
          <div className="finish-test" onClick={handleSubmit}>
            Закончить попытку
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestPage;
